/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { definePreset } from "@primeng/themes";
import Aura from "@primeng/themes/aura";

// QPilot/Autoship Brand Bridge Theme
export const QPilotBrandBridgePreset = definePreset(Aura, {
  primitive: {
    // QPilot Primary Blue
    blue: {
      50: "#eff6ff", // --qpilot-blue-50
      100: "#dbeafe", // --qpilot-blue-100
      200: "#bfdbfe",
      300: "#93c5fd", // --qpilot-blue-300
      400: "#60a5fa", // --qpilot-blue-400
      500: "#3b82f6", // --qpilot-blue-500
      600: "#2563eb", // --qpilot-blue-600
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a",
      950: "#172554",
    },

    // Autoship Secondary Purple
    violet: {
      50: "#f5f3ff", // --autoship-purple-50
      100: "#ede9fe", // --autoship-purple-100
      200: "#ddd6fe",
      300: "#c4b5fd", // --autoship-purple-300
      400: "#a78bfa", // --autoship-purple-400
      500: "#8b5cf6", // --autoship-purple-500
      600: "#7c3aed", // --autoship-purple-600
      700: "#6d28d9",
      800: "#5b21b6",
      900: "#4c1d95",
      950: "#2d1065",
    },

    // Success Green
    green: {
      50: "#ecfdf5",
      100: "#d1fae5", // --success-100
      200: "#a7f3d0",
      300: "#6ee7b7",
      400: "#34d399",
      500: "#10b981", // --success-500
      600: "#059669", // --success-600
      700: "#047857",
      800: "#065f46",
      900: "#064e3b",
      950: "#022c22",
    },

    // Warning Amber
    amber: {
      50: "#fefce8",
      100: "#fef3c7",
      200: "#fde68a",
      300: "#fcd34d",
      400: "#fbbf24",
      500: "#f59e0b",
      600: "#d97706",
      700: "#b45309",
      800: "#92400e",
      900: "#78350f",
      950: "#451a03",
    },

    // Danger Red
    red: {
      50: "#fef2f2",
      100: "#fee2e2",
      200: "#fecaca",
      300: "#fca5a5",
      400: "#f87171",
      500: "#ef4444",
      600: "#dc2626",
      700: "#b91c1c",
      800: "#991b1b",
      900: "#7f1d1d",
      950: "#450a0a",
    },

    // Info Blue
    sky: {
      50: "#f0f9ff",
      100: "#e0f2fe",
      200: "#bae6fd",
      300: "#7dd3fc",
      400: "#38bdf8",
      500: "#0ea5e9",
      600: "#0284c7",
      700: "#0369a1",
      800: "#075985",
      900: "#0c4a6e",
      950: "#082f49",
    },

    // Unified Neutral System
    gray: {
      50: "#f9fafb", // --neutral-50
      100: "#f3f4f6", // --neutral-100
      200: "#e5e7eb", // --neutral-200
      300: "#d1d5db", // --neutral-300
      400: "#9ca3af", // --neutral-400
      500: "#6b7280", // --neutral-500
      600: "#4b5563", // --neutral-600
      700: "#374151", // --neutral-700
      800: "#1f2937", // --neutral-800
      900: "#111827", // --neutral-900
      950: "#030712",
    },
  },

  semantic: {
    primary: {
      50: "{blue.50}",
      100: "{blue.100}",
      200: "{blue.200}",
      300: "{blue.300}",
      400: "{blue.400}",
      500: "{blue.500}",
      600: "{blue.600}",
      700: "{blue.700}",
      800: "{blue.800}",
      900: "{blue.900}",
      950: "{blue.950}",
    },

    colorScheme: {
      light: {
        primary: {
          color: "{blue.600}", // --qpilot-blue-600
          contrastColor: "#ffffff",
          hoverColor: "{blue.700}",
          activeColor: "{blue.800}",
        },

        secondary: {
          color: "{violet.600}", // --autoship-purple-600
          contrastColor: "#ffffff",
          hoverColor: "{violet.700}",
          activeColor: "{violet.800}",
        },

        success: {
          color: "{green.600}", // --success-600
          contrastColor: "#ffffff",
          hoverColor: "{green.700}",
          activeColor: "{green.800}",
        },

        info: {
          color: "{sky.600}",
          contrastColor: "#ffffff",
          hoverColor: "{sky.700}",
          activeColor: "{sky.800}",
        },

        warn: {
          color: "{amber.600}",
          contrastColor: "#ffffff",
          hoverColor: "{amber.700}",
          activeColor: "{amber.800}",
        },

        danger: {
          color: "{red.600}",
          contrastColor: "#ffffff",
          hoverColor: "{red.700}",
          activeColor: "{red.800}",
        },

        surface: {
          0: "#ffffff",
          50: "{gray.50}", // --neutral-50
          100: "{gray.100}", // --neutral-100
          200: "{gray.200}", // --neutral-200
          300: "{gray.300}", // --neutral-300
          400: "{gray.400}", // --neutral-400
          500: "{gray.500}", // --neutral-500
          600: "{gray.600}", // --neutral-600
          700: "{gray.700}", // --neutral-700
          800: "{gray.800}", // --neutral-800
          900: "{gray.900}", // --neutral-900
          950: "{gray.950}",
        },

        background: "{gray.50}", // --neutral-50
        earthy: "#FAF9F5", // Custom earthy background
        muted: "{gray.500}", // --neutral-500
        border: "{gray.200}", // --neutral-200
        disabled: "{gray.300}", // --neutral-300
        highlight: "{blue.100}", // --qpilot-blue-100
        shadow: "rgba(0, 0, 0, 0.1)",

        // Custom Subscription Business Colors
        custom: {
          // Subscription Status Colors
          subscriptionActive: "#059669", // --success-600
          subscriptionPaused: "#d97706", // amber.600
          subscriptionCancelled: "#dc2626", // red.600
          subscriptionProcessing: "#0284c7", // sky.600
          subscriptionFailed: "#ef4444", // red.500
          subscriptionTrial: "{violet.600}", // --autoship-purple-600
          subscriptionPending: "#f59e0b", // amber.500

          // Platform Integration Colors
          shopifyGreen: "#95bf47", // Official Shopify primary green
          shopifyDarkGreen: "#5e8e3e", // Official Shopify dark green
          woocommercePurple: "#720eec", // Official WooCommerce Purple 50
          woocommercePurple40: "#873eff", // WooCommerce Purple 40
          woocommercePurple70: "#5007aa", // WooCommerce Purple 70
          stripeBlue: "#635bff",
          paypalBlue: "#003087",
          klaviyoBlack: "#000000",
          zapierOrange: "#ff4a00",

          // Enterprise Metrics
          revenueGreen: "#059669", // --success-600
          churnRed: "#dc2626", // red.600
          growthBlue: "{blue.600}", // --qpilot-blue-600
          ltvPurple: "{violet.600}", // --autoship-purple-600
          retentionSuccess: "#10b981", // --success-500
          conversionAmber: "#d97706", // amber.600

          // Brand Bridge Gradients
          primaryGradient:
            "linear-gradient(135deg, {blue.600} 0%, {blue.700} 100%)",
          secondaryGradient:
            "linear-gradient(135deg, {violet.600} 0%, {violet.700} 100%)",
          successGradient: "linear-gradient(135deg, #059669 0%, #047857 100%)",
          heroGradient:
            "linear-gradient(135deg, {blue.600} 0%, {violet.600} 50%, #059669 100%)",
          bridgeGradient:
            "linear-gradient(135deg, {blue.600} 0%, {violet.600} 100%)",

          // Plan-specific colors for change-plan component
          planColors: {
            starter: {
              background: "#ecfdf5", // light green
              border: "#10b981", // green-500
              shadow: "0 8px 25px rgba(16, 185, 129, 0.15)",
              button: "#10b981",
              buttonHover: "#059669",
            },
            growth: {
              background: "#f5f3ff", // violet-50 (corrected)
              border: "#8b5cf6", // violet-500
              shadow: "0 8px 25px rgba(139, 92, 246, 0.15)",
              button: "#8b5cf6",
              buttonHover: "#7c3aed",
            },
            marketer: {
              background: "#eff6ff", // light blue
              border: "#3b82f6", // blue-500
              shadow: "0 8px 25px rgba(59, 130, 246, 0.15)",
              button: "#3b82f6",
              buttonHover: "#2563eb",
            },
            default: {
              background: "#f9fafb", // neutral-50
              border: "#d1d5db", // neutral-300
              shadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
              button: "#6b7280",
              buttonHover: "#4b5563",
            },
          },
        },
      },
    },
  },

  // Component Customizations with Spacing, Sizing & Shadows
  components: {
    button: {
      root: {
        // borderRadius: "8px",
        // fontWeight: "600",
        // transition: "all 0.2s ease",
        // paddingX: "16px",
        // paddingY: "12px",
      },
      primary: {
        background: "linear-gradient(135deg, {blue.600} 0%, {blue.700} 100%)",
        boxShadow: "0 4px 14px rgba(37, 99, 235, 0.3)",
      },
      secondary: {
        background:
          "linear-gradient(135deg, {violet.600} 0%, {violet.700} 100%)",
        boxShadow: "0 4px 14px rgba(124, 58, 237, 0.3)",
      },
    },

    // inputtext: {
    //   root: {
    //     borderRadius: "8px",
    //     borderWidth: "2px",
    //     padding: "12px 16px",
    //     fontSize: "16px",
    //     transition: "all 0.2s ease",
    //   },
    //   focus: {
    //     borderColor: "{primary.600}",
    //     boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.1)",
    //   },
    // },

    // card: {
    //   root: {
    //     borderRadius: "12px",
    //     boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    //     border: "1px solid {surface.200}",
    //     padding: "24px",
    //   },
    // },

    // datatable: {
    //   root: {
    //     borderRadius: "12px",
    //     overflow: "hidden",
    //     boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    //   },
    //   header: {
    //     background: "{surface.50}",
    //     borderColor: "{surface.200}",
    //     fontWeight: "600",
    //     color: "{surface.700}",
    //     padding: "16px 24px",
    //   },
    //   row: {
    //     hoverBackground: "{primary.50}",
    //     padding: "12px 24px",
    //   },
    // },

    // menu: {
    //   root: {
    //     borderRadius: "8px",
    //     boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    //     padding: "8px",
    //   },
    //   item: {
    //     hoverBackground: "{primary.50}",
    //     activeBackground: "{primary.100}",
    //     borderRadius: "6px",
    //     padding: "8px 12px",
    //     margin: "2px 0",
    //   },
    // },

    // dialog: {
    //   root: {
    //     borderRadius: "16px",
    //     boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    //   },
    //   header: {
    //     background: "{surface.50}",
    //     borderColor: "{surface.200}",
    //     padding: "24px 32px 16px",
    //   },
    //   content: {
    //     padding: "0 32px 24px",
    //   },
    // },

    // panel: {
    //   root: {
    //     borderRadius: "12px",
    //     boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
    //   },
    //   header: {
    //     padding: "16px 20px",
    //     borderRadius: "12px 12px 0 0",
    //   },
    //   content: {
    //     padding: "20px",
    //   },
    // },

    // toolbar: {
    //   root: {
    //     borderRadius: "8px",
    //     padding: "12px 16px",
    //     gap: "12px",
    //   },
    // },

    // toast: {
    //   root: {
    //     borderRadius: "8px",
    //     boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    //     padding: "16px 20px",
    //     minWidth: "300px",
    //   },
    // },

    // badge: {
    //   root: {
    //     borderRadius: "12px",
    //     fontSize: "12px",
    //     fontWeight: "600",
    //     padding: "4px 8px",
    //     minWidth: "20px",
    //     height: "20px",
    //   },
    // },

    // chip: {
    //   root: {
    //     borderRadius: "16px",
    //     padding: "6px 12px",
    //     fontSize: "14px",
    //     fontWeight: "500",
    //   },
    // },
  },
});

export default QPilotBrandBridgePreset;
